/* Sockelleisten Calculator
   - Simplified calculator for Sockelleisten products
   - lfm based, no Verschnitt
   - Includes both Sockelleisten Zubehör and Verlegezubehör modals
*/

(() => {
  'use strict';

  const SELECTORS = {
    root: '#sockelleisten-calculator[data-fc="root"]',
    variantJsonTextarea: 'textarea[data-current-variant-json]',
    productForm: 'form[action^="/cart/add"]',
    qtyInput: 'input[name="quantity"]',

    atcButtonInForm: 'button[type="submit"][name="add"], button[type="submit"][data-add-to-cart], button.add-to-cart',
    atcButtonInRoot: 'button[name="add"], button[data-add-to-cart], button.add-to-cart, button[type="submit"]',

    // inputs
    lfmInput: '[data-fc-input="lfm"]',

    // outputs
    outStueck: '[data-fc-out="stueck"]',
    outLfmPerStueckDisplay: '[data-fc-out="lfm_per_stueck_display"]',
    outUnitPrice: '[data-fc-out="unit_price"]',
    outUvpPerLfm: '[data-fc-out="uvp_per_lfm"]',
    outBlockTotal: '[data-fc-out="block_total"]',
    outGrandTotal: '[data-fc-out="grand_total"]',
    outGrandCompareTotal: '[data-fc-out="grand_compare_total"]',
    outAddonsSockelSelected: '[data-fc-out="addons_sockel_selected"]',
    outAddonsVerlegeSelected: '[data-fc-out="addons_verlege_selected"]',

    // actions
    actionBtn: '[data-fc-action]',

    // modal
    modal: '[data-fc-modal]',
    modalClose: '[data-fc-modal-close]',
    modalTitle: '[data-fc-modal-title]',
    modalSubtitle: '[data-fc-modal-subtitle]',
    modalGrid: '[data-fc-modal-grid]',
    modalHint: '[data-fc-modal-hint]',
    modalApply: '[data-fc-modal-apply]',

    accessoriesJson: '[data-fc-accessories-json]',

    // Impulse price fallbacks
    domPrice: '[data-product-price]',
    domCompare: '[data-compare-price]',
  };

  function clampNumber(n, min, max) {
    const x = Number.isFinite(n) ? n : 0;
    return Math.min(Math.max(x, min), max);
  }

  function ceilInt(n) {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    return Math.max(0, Math.ceil(x));
  }

  function roundTo(n, decimals = 2) {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    const m = Math.pow(10, decimals);
    return Math.round(x * m) / m;
  }

  function safeJsonParse(str) {
    try { return JSON.parse(str); } catch (_) { return null; }
  }

  function getCurrencyFromShopify() {
    return (window.Shopify && window.Shopify.currency && (window.Shopify.currency.active || window.Shopify.currency.currency)) || 'EUR';
  }

  function formatMoneyFromCents(cents, currency = 'EUR', locale = document.documentElement.lang || 'de-DE') {
    const c = Number(cents);
    const safeCents = Number.isFinite(c) ? c : 0;
    const value = safeCents / 100;
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value);
    } catch (_) {
      return `${value.toFixed(2)} €`;
    }
  }

  function formatNumber(n, maxFrac = 2, locale = document.documentElement.lang || 'de-DE') {
    const x = Number(n);
    if (!Number.isFinite(x)) return '0';
    try {
      return new Intl.NumberFormat(locale, { minimumFractionDigits: 0, maximumFractionDigits: maxFrac }).format(x);
    } catch (_) {
      return String(x);
    }
  }

  function parseMoneyToCents(input) {
    if (input == null) return 0;

    if (typeof input === 'number' && Number.isFinite(input)) {
      if (Number.isInteger(input) && input >= 100) return input;
      if (input < 1000) return Math.round(input * 100);
      return Math.round(input);
    }

    const str = String(input).replace(/\s+/g, '').replace(/[^\d,.\-]/g, '');
    if (!str) return 0;

    let normalized = str;
    const hasComma = normalized.includes(',');
    const hasDot = normalized.includes('.');

    if (hasComma && hasDot) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else if (hasComma && !hasDot) {
      normalized = normalized.replace(',', '.');
    }

    const num = Number(normalized);
    if (!Number.isFinite(num)) return 0;

    if (Number.isInteger(num) && num >= 100) return num;
    return Math.round(num * 100);
  }

  // Bundle ID helpers
  function fcBundleStorageKey(productId, handle) {
    const pid = String(productId || '').trim();
    const h = String(handle || '').trim();
    return `fc_sockel_bundle:${pid || h || '0'}`;
  }

  function fcMakeBundleId() {
    try {
      if (crypto && typeof crypto.randomUUID === 'function') return `fcs_${crypto.randomUUID()}`;
    } catch (_) {}
    return `fcs_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }

  function fcGetOrCreateBundleId(productId, handle) {
    const key = fcBundleStorageKey(productId, handle);
    let id = '';
    try { id = sessionStorage.getItem(key) || ''; } catch (_) {}
    if (!id) {
      id = fcMakeBundleId();
      try { sessionStorage.setItem(key, id); } catch (_) {}
    }
    return id;
  }

  function openMiniCart() {
    const t = window.theme;

    try {
      if (t && typeof t.CartDrawer === 'function') {
        if (!t.__fcCartDrawerInstance) {
          t.__fcCartDrawerInstance = new t.CartDrawer();
          if (typeof t.__fcCartDrawerInstance.init === 'function') {
            try { t.__fcCartDrawerInstance.init(); } catch (_) {}
          }
        }
        if (t.__fcCartDrawerInstance && typeof t.__fcCartDrawerInstance.open === 'function') {
          t.__fcCartDrawerInstance.open();
          return true;
        }
      }
    } catch (_) {}

    const triggers = [
      '[aria-controls="CartDrawer"]',
      '.js-drawer-open-cart',
      '.site-nav__link--icon[aria-controls="CartDrawer"]'
    ];

    for (const sel of triggers) {
      const el = document.querySelector(sel);
      if (el && typeof el.click === 'function') {
        try { el.click(); return true; } catch (_) {}
      }
    }

    ['theme:cart:open','cart:open','drawer:open','ajaxProduct:added','cart:refresh','cart:updated'].forEach((name) => {
      try { document.dispatchEvent(new CustomEvent(name, { bubbles: true })); } catch (_) {}
    });

    return false;
  }

  // Cart helpers
  async function fetchCartJson() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    if (!res.ok) throw new Error(`Failed /cart.js (${res.status})`);
    return res.json();
  }

  async function cartChangeByKey(lineKey, quantity, properties) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: Number(quantity) || 0, properties: properties || {} })
    });
    if (!res.ok) throw new Error(`HTTP ${res.status} (change)`);
    return res.json();
  }

  async function cartAddItems(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items })
    });
    if (!res.ok) throw new Error(`HTTP ${res.status} (add)`);
    return res.json();
  }

  class SockelleistenCalculator {
    constructor(root) {
      this.root = root;
      this.currency = getCurrencyFromShopify();

      this.productId = root.dataset.productId || '';
      this.handle = root.dataset.productHandle || '';
      this.initialVariantId = Number(root.dataset.initialVariantId || 0) || 0;

      this.bundleId = fcGetOrCreateBundleId(this.productId, this.handle);

      this.packageSize = Number(String(root.dataset.packageSize || '0').replace(',', '.')) || 0;
      this.lfmPerStueck = Number(String(root.dataset.lfmPerStueck || '1').replace(',', '.')) || 1;

      this.initialVariantPriceCents = parseMoneyToCents(root.dataset.initialVariantPrice || 0);
      this.initialVariantCompareAtCents = parseMoneyToCents(root.dataset.initialVariantCompareAt || 0);

      this.isSyncing = false;
      this.currentVariant = null;

      // Elements
      this.lfmInput = root.querySelector(SELECTORS.lfmInput);

      this.outStueck = root.querySelector(SELECTORS.outStueck);
      this.outLfmPerStueckDisplay = root.querySelector(SELECTORS.outLfmPerStueckDisplay);
      this.outUnitPrice = root.querySelector(SELECTORS.outUnitPrice);
      this.outUvpPerLfm = root.querySelector(SELECTORS.outUvpPerLfm);
      this.outPricePerStueck = root.querySelector('[data-fc-out="price_per_stueck"]');
      this.outBlockTotal = root.querySelector(SELECTORS.outBlockTotal);
      this.outGrandTotal = root.querySelector(SELECTORS.outGrandTotal);
      this.outGrandCompareTotal = root.querySelector(SELECTORS.outGrandCompareTotal);
      this.outAddonsSockelSelected = root.querySelector(SELECTORS.outAddonsSockelSelected);
      this.outAddonsVerlegeSelected = root.querySelector(SELECTORS.outAddonsVerlegeSelected);

      // Modal
      this.modal = root.querySelector(SELECTORS.modal);
      this.modalTitle = root.querySelector(SELECTORS.modalTitle);
      this.modalSubtitle = root.querySelector(SELECTORS.modalSubtitle);
      this.modalGrid = root.querySelector(SELECTORS.modalGrid);
      this.modalHint = root.querySelector(SELECTORS.modalHint);
      this.modalApply = root.querySelector(SELECTORS.modalApply);

      this.activeModalType = null;
      this.modalItems = [];
      this.modalQty = new Map();

      this.accessoriesCatalog = { sockel: [], verlege: [] };
      this.accessoriesByVariant = new Map();

      // Form
      this.form = this.findProductForm();
      this.qtyInput = this.form ? this.form.querySelector(SELECTORS.qtyInput) : null;
      this.atcButton =
        this.root.querySelector(SELECTORS.atcButtonInRoot) ||
        (this.form ? this.form.querySelector(SELECTORS.atcButtonInForm) : null);

      this.variantTextarea = document.querySelector(SELECTORS.variantJsonTextarea);

      this.addonsKey = this.makeAddonsKey(this.initialVariantId);

      this.state = {
        lfm: 1,
        stueck: 0,
        pricePerLfmCents: 0,
        pricePerStueckCents: 0,
        blockTotalCents: 0,
        compareTotalCents: 0,
        addonsTotalCents: 0,
        addonsCompareTotalCents: 0,
        grandTotalCents: 0,
        compareAllTotalCents: 0,
      };
    }

    getDomPriceCents() {
      const scope = this.root.closest('.product-single') || document;
      const el = scope.querySelector(SELECTORS.domPrice);
      return parseMoneyToCents(el ? el.textContent : '');
    }

    getDomCompareAtCents() {
      const scope = this.root.closest('.product-single') || document;
      const el = scope.querySelector(SELECTORS.domCompare);
      return parseMoneyToCents(el ? el.textContent : '');
    }

    normalizeCurrentVariantPrices() {
      if (!this.currentVariant) return;

      const domPrice = this.getDomPriceCents();
      const domCompare = this.getDomCompareAtCents();

      const vPrice = parseMoneyToCents(this.currentVariant.price);
      const vCompare = parseMoneyToCents(this.currentVariant.compare_at_price);

      this.currentVariant.price = vPrice || this.initialVariantPriceCents || domPrice || 0;
      this.currentVariant.compare_at_price = vCompare || this.initialVariantCompareAtCents || domCompare || 0;
    }

    init() {
      if (!this.lfmInput) return;

      if (this.qtyInput) {
        this.qtyInput.min = '0';
        this.qtyInput.value = '0';
      }

      this.lfmInput.value = '1';

      this.currentVariant = this.getCurrentVariantSafe() || {
        id: this.initialVariantId,
        price: this.initialVariantPriceCents || 0,
        compare_at_price: this.initialVariantCompareAtCents || 0
      };

      this.normalizeCurrentVariantPrices();

      this.addonsKey = this.makeAddonsKey(this.currentVariant?.id || this.initialVariantId);

      this.accessoriesCatalog = this.readAccessoriesCatalog();
      this.accessoriesByVariant = this.indexAccessoriesByVariant(this.accessoriesCatalog);

      this.applyAddonsFromStorage();

      this.recomputeAndRender({ source: 'init', syncQty: true });

      this.bindControlButtons();
      this.bindInputs();
      this.bindVariantChanges();
      this.bindFormIntercept();
      this.bindAtcClickFallback();
      this.bindQtyObserver();
      this.bindModalEvents();

      this.updateAtcState();
    }

    // Accessories
    readAccessoriesCatalog() {
      const el = this.root.querySelector(SELECTORS.accessoriesJson);
      if (!el) return { sockel: [], verlege: [] };
      const raw = (el.textContent || '').trim();
      const parsed = raw ? safeJsonParse(raw) : null;
      if (!parsed || typeof parsed !== 'object') return { sockel: [], verlege: [] };
      return {
        sockel: Array.isArray(parsed.sockel) ? parsed.sockel : [],
        verlege: Array.isArray(parsed.verlege) ? parsed.verlege : [],
      };
    }

    indexAccessoriesByVariant(cat) {
      const m = new Map();
      ['sockel', 'verlege'].forEach((t) => {
        (cat[t] || []).forEach((it) => {
          const vid = Number(it.variantId || 0);
          if (vid) m.set(String(vid), { ...it, type: t });
        });
      });
      return m;
    }

    makeAddonsKey(variantId) {
      const pid = this.productId || '0';
      const vid = variantId || '0';
      return `fcs_addons:${pid}:${vid}`;
    }

    normalizeAddonsBlock(value) {
      if (!value) return {};
      if (Array.isArray(value)) {
        const obj = {};
        value.map(String).forEach((vid) => { obj[String(vid)] = 1; });
        return obj;
      }
      if (typeof value === 'object') {
        const obj = {};
        Object.keys(value).forEach((k) => {
          const q = Number(value[k]);
          const qq = Number.isFinite(q) ? Math.max(0, Math.floor(q)) : 0;
          if (qq > 0) obj[String(k)] = qq;
        });
        return obj;
      }
      return {};
    }

    readAddonsStore() {
      if (!this.addonsKey) return { sockel: {}, verlege: {} };
      const raw = localStorage.getItem(this.addonsKey);
      const parsed = raw ? safeJsonParse(raw) : null;
      if (!parsed || typeof parsed !== 'object') return { sockel: {}, verlege: {} };
      return {
        sockel: this.normalizeAddonsBlock(parsed.sockel),
        verlege: this.normalizeAddonsBlock(parsed.verlege),
      };
    }

    writeAddonsStore(store) {
      if (!this.addonsKey) return;
      const safe = store || { sockel: {}, verlege: {} };
      localStorage.setItem(this.addonsKey, JSON.stringify(safe));
    }

    applyAddonsFromStorage() {
      const store = this.readAddonsStore();
      this.renderAddonSummary('sockel', store.sockel);
      this.renderAddonSummary('verlege', store.verlege);
    }

    renderAddonSummary(type, selectedMap) {
      const el = type === 'sockel' ? this.outAddonsSockelSelected : this.outAddonsVerlegeSelected;
      if (!el) return;

      const map = selectedMap && typeof selectedMap === 'object' ? selectedMap : {};
      const ids = Object.keys(map);

      if (!ids.length) {
        el.hidden = true;
        el.innerHTML = '';
        return;
      }

      const items = ids
        .map((vid) => {
          const it = this.accessoriesByVariant.get(String(vid));
          if (!it) return null;
          const qty = Number(map[String(vid)] || 0) || 0;
          if (qty <= 0) return null;
          return { it, qty };
        })
        .filter(Boolean);

      if (!items.length) {
        el.hidden = true;
        el.innerHTML = '';
        return;
      }

      el.hidden = false;

      el.innerHTML = items.map(({ it, qty }) => {
        const title = String(it.title || '');
        const img = String(it.image || '');
        const unit = Number(it.priceCents || 0) || 0;
        const total = unit * qty;
        const price = formatMoneyFromCents(total, this.currency);

        const imgHtml = img
          ? `<img class="fc-addon-line__img" src="${img}" alt="${title}" loading="lazy">`
          : `<div class="fc-addon-line__imgPh" aria-hidden="true"></div>`;

        return `
          <div class="fc-addon-line">
            ${imgHtml}
            <div class="fc-addon-line__title">${qty}× ${title}</div>
            <div class="fc-addon-line__price">${price}</div>
          </div>
        `;
      }).join('');
    }

    // Modal methods
    resolveUnitLabel(item) {
      const title = String(item?.title || '').toLowerCase().trim();
      if (title.includes('montageclips')) return 'Pakete a 30 St.';
      if (title.includes('abstandskeile')) return 'Pakete a 20 St.';
      if (title.includes('innenecke') || title.includes('endkappen') || title.includes('aussenecke') || title.includes('laminatschneider')) return 'St.';
      return 'St.';
    }

    getRealCatalog(type) {
      const t = type === 'sockel' ? 'sockel' : 'verlege';
      const title = t === 'sockel' ? 'Sockelleisten Zubehör' : 'Verlegezubehör';

      const items = (this.accessoriesCatalog && this.accessoriesCatalog[t])
        ? this.accessoriesCatalog[t].map((it) => ({
            id: String(it.variantId),
            title: it.title || '',
            priceCents: Number(it.priceCents || 0) || 0,
            compareAtCents: Number(it.compareAtCents || 0) || 0,
            image: it.image || '',
            unitLabel: it.unitLabel || 'St.'
          }))
        : [];

      return { title, subtitle: '', items };
    }

    openModal(type) {
      if (!this.modal) return;

      const t = type === 'sockel' ? 'sockel' : 'verlege';
      this.activeModalType = t;

      const store = this.readAddonsStore();
      const selectedMap = t === 'sockel' ? store.sockel : store.verlege;

      this.modalQty = new Map();
      Object.keys(selectedMap || {}).forEach((vid) => {
        const q = Number(selectedMap[String(vid)] || 0) || 0;
        if (q > 0) this.modalQty.set(String(vid), q);
      });

      const catalog = this.getRealCatalog(t);
      if (this.modalTitle) this.modalTitle.textContent = catalog.title;
      if (this.modalSubtitle) this.modalSubtitle.textContent = catalog.subtitle;

      this.modalItems = catalog.items.slice();
      this.renderModalGrid();

      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('fc-modal-open');
      document.body.classList.add('fc-modal-open');

      this.updateModalHint();
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('fc-modal-open');
      document.body.classList.remove('fc-modal-open');
      this.activeModalType = null;
      this.modalItems = [];
      this.modalQty = new Map();
    }

    updateModalHint() {
      if (!this.modalHint) return;
      const count = Array.from(this.modalQty.values()).filter((q) => (Number(q) || 0) > 0).length;
      this.modalHint.textContent = `${count} ausgewählt`;
    }

    getModalItemQty(id) {
      const v = this.modalQty.get(String(id));
      const n = Number(v);
      return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
    }

    setModalItemQty(id, qty) {
      const q = Number(qty);
      const qq = Number.isFinite(q) ? Math.max(0, Math.floor(q)) : 0;
      const key = String(id);
      if (qq <= 0) this.modalQty.delete(key);
      else this.modalQty.set(key, qq);
    }

    updateModalCardUI(cardEl, it) {
      if (!cardEl || !it) return;

      const id = String(it.id);
      const qty = this.getModalItemQty(id);

      cardEl.classList.toggle('is-selected', qty > 0);

      const input = cardEl.querySelector('[data-fc-modal-qty-input]');
      if (input) input.value = String(qty);

      const priceEl = cardEl.querySelector('.fc-modalCard__price');
      if (priceEl) {
        const unit = Number(it.priceCents || 0) || 0;
        const showCents = qty > 0 ? unit * qty : unit;
        priceEl.textContent = formatMoneyFromCents(showCents, this.currency);
      }
    }

    renderModalGrid() {
      if (!this.modalGrid) return;

      const items = this.modalItems || [];

      const cardHtml = (it) => {
        const id = String(it.id);
        const qty = this.getModalItemQty(id);
        const unitLabel = this.resolveUnitLabel(it);

        const unit = Number(it.priceCents || 0) || 0;
        const showCents = qty > 0 ? unit * qty : unit;

        return `
          <div class="fc-modalCard ${qty > 0 ? 'is-selected' : ''}" data-fc-modal-item="${id}">
            <div class="fc-modalCard__img">
              ${it.image ? `<img src="${it.image}" alt="${it.title}">` : `<div class="fc-modalCard__imgPh"></div>`}
            </div>

            <div class="fc-modalCard__meta">
              <div class="fc-modalCard__title">${it.title}</div>
              <div class="fc-modalCard__price">${formatMoneyFromCents(showCents, this.currency)}</div>

              <div class="fc-modalCard__qtyRow">
                <div class="fc-controls fc-controls--modal">
                  <button type="button" class="fc-controls__btn" data-fc-modal-qty-minus="${id}">-</button>
                  <input class="fc-controls__input" type="number" value="${qty}" min="0" step="1" data-fc-modal-qty-input="${id}">
                  <button type="button" class="fc-controls__btn" data-fc-modal-qty-plus="${id}">+</button>
                </div>
                <span class="fc-modalCard__unit">${unitLabel}</span>
              </div>
            </div>
          </div>
        `;
      };

      this.modalGrid.innerHTML = items.map(cardHtml).join('');
    }

    applyModalSelection() {
      const t = this.activeModalType;
      if (!t) return;

      const store = this.readAddonsStore();
      const payload = {};

      this.modalQty.forEach((q, vid) => {
        const qq = Number(q) || 0;
        if (qq > 0) payload[String(vid)] = qq;
      });

      if (t === 'sockel') store.sockel = payload;
      else store.verlege = payload;

      this.writeAddonsStore(store);
      this.applyAddonsFromStorage();

      this.recomputeAndRender({ source: 'addons_apply', syncQty: true });
      this.closeModal();
    }

    bindModalEvents() {
      if (!this.modal) return;

      this.modal.addEventListener('click', (e) => {
        const close = e.target.closest(SELECTORS.modalClose);
        if (close) {
          e.preventDefault();
          this.closeModal();
          return;
        }

        if (e.target && e.target.matches && e.target.matches('.fc-modal__backdrop')) {
          e.preventDefault();
          this.closeModal();
          return;
        }

        const minusBtn = e.target.closest('[data-fc-modal-qty-minus]');
        if (minusBtn) {
          e.preventDefault();
          const id = String(minusBtn.getAttribute('data-fc-modal-qty-minus') || '');
          if (!id) return;

          const cur = this.getModalItemQty(id);
          this.setModalItemQty(id, Math.max(0, cur - 1));

          const card = minusBtn.closest('[data-fc-modal-item]');
          const it = (this.modalItems || []).find(x => String(x.id) === String(id));
          this.updateModalCardUI(card, it);
          this.updateModalHint();
          return;
        }

        const plusBtn = e.target.closest('[data-fc-modal-qty-plus]');
        if (plusBtn) {
          e.preventDefault();
          const id = String(plusBtn.getAttribute('data-fc-modal-qty-plus') || '');
          if (!id) return;

          const cur = this.getModalItemQty(id);
          this.setModalItemQty(id, cur + 1);

          const card = plusBtn.closest('[data-fc-modal-item]');
          const it = (this.modalItems || []).find(x => String(x.id) === String(id));
          this.updateModalCardUI(card, it);
          this.updateModalHint();
          return;
        }
      });

      this.modal.addEventListener('input', (e) => {
        const input = e.target && e.target.closest && e.target.closest('[data-fc-modal-qty-input]');
        if (!input) return;

        const id = String(input.getAttribute('data-fc-modal-qty-input') || '');
        if (!id) return;

        const raw = String(input.value || '').replace(',', '.');
        const v = Number(raw);
        const qty = Number.isFinite(v) ? Math.max(0, Math.floor(v)) : 0;

        this.setModalItemQty(id, qty);

        const card = input.closest('[data-fc-modal-item]');
        const it = (this.modalItems || []).find(x => String(x.id) === String(id));
        this.updateModalCardUI(card, it);
        this.updateModalHint();
      });

      if (this.modalApply) {
        this.modalApply.addEventListener('click', (e) => {
          e.preventDefault();
          this.applyModalSelection();
        });
      }

      document.addEventListener('keydown', (e) => {
        if (!this.modal || this.modal.hidden) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          this.closeModal();
        }
      });
    }

    // Core methods
    findProductForm() {
      const closestForm = this.root.closest('.product-single__meta')?.querySelector(SELECTORS.productForm);
      if (closestForm) return closestForm;
      const all = document.querySelectorAll(SELECTORS.productForm);
      return all && all.length ? all[0] : null;
    }

    getCurrentVariantSafe() {
      if (this.variantTextarea) {
        const json = this.variantTextarea.value || this.variantTextarea.textContent || '';
        const parsed = safeJsonParse(json);
        if (parsed && parsed.id) return parsed;
      }
      if (window.meta && window.meta.product && window.meta.product.variants) {
        const v = window.meta.product.variants.find(x => x.id === this.initialVariantId);
        if (v) return v;
      }
      return null;
    }

    getIntFromInput(inputEl) {
      if (!inputEl) return 0;
      const raw = String(inputEl.value || '').replace(',', '.');
      const v = Number(raw);
      return ceilInt(clampNumber(v, 0, 999999));
    }

    setIntToInput(inputEl, value) {
      if (!inputEl) return;
      inputEl.value = String(ceilInt(value));
    }

    getStueckFromQtyInput() {
      if (!this.qtyInput) return ceilInt(this.state?.stueck || 0);
      const v = Number(String(this.qtyInput.value || '0').replace(',', '.'));
      return ceilInt(clampNumber(v, 0, 999999));
    }

    setStueckToQtyInput(stueck) {
      if (!this.qtyInput) return;
      this.qtyInput.value = String(ceilInt(stueck));
      this.qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    recomputeAndRender({ source, syncQty }) {
      if (this.isSyncing) return;

      if (!this.currentVariant) {
        this.currentVariant = this.getCurrentVariantSafe() || { id: this.initialVariantId, price: 0, compare_at_price: 0 };
      }
      this.normalizeCurrentVariantPrices();

      const variant = this.currentVariant;
      const lfmPerStueck = Number(this.lfmPerStueck) || 1;

      const lfm = this.getIntFromInput(this.lfmInput);
      const stueck = lfm > 0 ? Math.ceil(lfm / lfmPerStueck) : 0;

      // Price per Stück (from variant)
      const pricePerStueckCents = parseMoneyToCents(variant?.price) || 0;
      
      // Price per lfm = price per Stück / lfm per Stück
      const pricePerLfmCents = (lfmPerStueck > 0 && pricePerStueckCents > 0)
        ? Math.round(pricePerStueckCents / lfmPerStueck)
        : 0;

      const blockTotalCents = stueck * pricePerStueckCents;

      const compareAtStueckCents = parseMoneyToCents(variant?.compare_at_price) || 0;
      const compareTotalCents = compareAtStueckCents ? (stueck * compareAtStueckCents) : 0;

      // Addons
      const storeAddons = this.readAddonsStore();

      const sumByMap = (obj) => {
        const map = obj && typeof obj === 'object' ? obj : {};
        let total = 0;
        let compare = 0;

        Object.keys(map).forEach((vid) => {
          const qty = Number(map[String(vid)] || 0) || 0;
          if (qty <= 0) return;

          const it = this.accessoriesByVariant.get(String(vid));
          if (!it) return;

          total += (Number(it.priceCents || 0) || 0) * qty;
          compare += (Number(it.compareAtCents || 0) || 0) * qty;
        });

        return { total, compare };
      };

      const sockelAdd = sumByMap(storeAddons.sockel);
      const verlegeAdd = sumByMap(storeAddons.verlege);

      const addonsTotalCents = sockelAdd.total + verlegeAdd.total;
      const addonsCompareTotalCents = sockelAdd.compare + verlegeAdd.compare;

      const grandTotalCents = blockTotalCents + addonsTotalCents;
      const compareAllTotalCents = compareTotalCents + addonsCompareTotalCents;

      this.state = {
        ...this.state,
        lfm,
        stueck,
        pricePerLfmCents,
        pricePerStueckCents,
        blockTotalCents,
        compareTotalCents,
        addonsTotalCents,
        addonsCompareTotalCents,
        grandTotalCents,
        compareAllTotalCents,
      };

      if (syncQty) {
        this.isSyncing = true;
        this.setStueckToQtyInput(stueck);
        this.isSyncing = false;
      }

      this.render();
      this.updateAtcState();
    }

    render() {
      const s = this.state;
      const lfmPerStueck = Number(this.lfmPerStueck) || 1;

      if (this.outStueck) this.outStueck.textContent = String(s.stueck);
      
      // Display lfm per Stück with proper formatting (e.g., "2,38")
      if (this.outLfmPerStueckDisplay) this.outLfmPerStueckDisplay.textContent = formatNumber(lfmPerStueck, 2);

      // Red header price = price per lfm
      if (this.outUnitPrice) this.outUnitPrice.textContent = formatMoneyFromCents(s.pricePerLfmCents || 0, this.currency);

      if (this.outPricePerStueck) this.outPricePerStueck.textContent = formatMoneyFromCents(s.pricePerStueckCents || 0, this.currency);

      // UVP per lfm
      if (this.outUvpPerLfm) {
        const compareAtStueck = parseMoneyToCents(this.currentVariant?.compare_at_price) || 0;
        const uvpPerLfm = (lfmPerStueck > 0 && compareAtStueck > 0) ? Math.round(compareAtStueck / lfmPerStueck) : 0;
        const show = uvpPerLfm > (s.pricePerLfmCents || 0);

        if (!show || !uvpPerLfm) {
          this.outUvpPerLfm.hidden = true;
          this.outUvpPerLfm.textContent = '';
        } else {
          this.outUvpPerLfm.hidden = false;
          this.outUvpPerLfm.textContent = `UVP ${formatMoneyFromCents(uvpPerLfm, this.currency)}`;
        }
      }

      if (this.outBlockTotal) this.outBlockTotal.textContent = formatMoneyFromCents(s.blockTotalCents || 0, this.currency);

      if (this.outGrandTotal) this.outGrandTotal.textContent = formatMoneyFromCents(s.grandTotalCents || 0, this.currency);

      if (this.outGrandCompareTotal) {
        const ca = Number(s.compareAllTotalCents) || 0;
        const fin = Number(s.grandTotalCents) || 0;
        const show = ca > fin && fin > 0;
        if (!show) {
          this.outGrandCompareTotal.hidden = true;
          this.outGrandCompareTotal.textContent = '';
        } else {
          this.outGrandCompareTotal.hidden = false;
          this.outGrandCompareTotal.textContent = formatMoneyFromCents(ca, this.currency);
        }
      }
    }

    updateAtcState() {
      if (!this.atcButton) return;
      const qty = ceilInt(this.state?.stueck || 0);
      const canAdd = qty > 0;
      this.atcButton.disabled = !canAdd;
      this.atcButton.setAttribute('aria-disabled', String(!canAdd));
    }

    bindControlButtons() {
      this.root.addEventListener('click', (e) => {
        const btn = e.target.closest(SELECTORS.actionBtn);
        if (!btn) return;

        const action = String(btn.dataset.fcAction || '');

        if (action === 'add_sockel_accessories') {
          e.preventDefault(); e.stopPropagation();
          this.openModal('sockel');
          return;
        }

        if (action === 'add_verlege_accessories') {
          e.preventDefault(); e.stopPropagation();
          this.openModal('verlege');
          return;
        }

        const incDec = (inputEl, dir, step = 1) => {
          if (!inputEl) return;
          e.preventDefault();
          const cur = this.getIntFromInput(inputEl);
          const next = dir === 'plus' ? cur + step : cur - step;
          this.setIntToInput(inputEl, Math.max(0, next));
        };

        if (action === 'lfm_minus') { incDec(this.lfmInput, 'minus', 1); this.recomputeAndRender({ source: 'lfm_minus', syncQty: true }); }
        if (action === 'lfm_plus')  { incDec(this.lfmInput, 'plus', 1);  this.recomputeAndRender({ source: 'lfm_plus',  syncQty: true }); }
      });
    }

    bindInputs() {
      const bindIntInput = (inputEl, onChange) => {
        if (!inputEl) return;
        inputEl.addEventListener('input', () => onChange());
        inputEl.addEventListener('change', () => {
          const v = this.getIntFromInput(inputEl);
          this.setIntToInput(inputEl, v);
          onChange();
        });
      };

      bindIntInput(this.lfmInput, () => this.recomputeAndRender({ source: 'lfm_input', syncQty: true }));

      if (this.qtyInput) {
        this.qtyInput.addEventListener('input', () => this.recomputeAndRender({ source: 'qty', syncQty: false }));
        this.qtyInput.addEventListener('change', () => this.recomputeAndRender({ source: 'qty', syncQty: false }));
      }
    }

    bindVariantChanges() {
      if (this.variantTextarea) {
        let last = this.variantTextarea.value;
        window.setInterval(() => {
          const cur = this.variantTextarea.value;
          if (cur !== last) { last = cur; this.onVariantUpdated(); }
        }, 350);
      }

      const handler = () => this.onVariantUpdated();
      document.addEventListener('variantChange', handler);
      document.addEventListener('variantSKUChange', handler);
      document.addEventListener('variant:change', handler);
    }

    onVariantUpdated() {
      const v = this.getCurrentVariantSafe();
      if (!v || !v.id) return;

      this.currentVariant = v;
      this.normalizeCurrentVariantPrices();

      this.addonsKey = this.makeAddonsKey(v.id);

      this.accessoriesCatalog = this.readAccessoriesCatalog();
      this.accessoriesByVariant = this.indexAccessoriesByVariant(this.accessoriesCatalog);

      this.applyAddonsFromStorage();

      this.recomputeAndRender({ source: 'variant', syncQty: true });
    }

    bindQtyObserver() {
      if (!this.form) return;

      const obs = new MutationObserver(() => {
        const q = this.form.querySelector(SELECTORS.qtyInput);
        if (q && q !== this.qtyInput) {
          this.qtyInput = q;
          this.qtyInput.min = '0';
          this.bindInputs();
          this.recomputeAndRender({ source: 'qty', syncQty: false });
        }
      });

      obs.observe(this.form, { childList: true, subtree: true });
    }

    bindFormIntercept() {
      if (!this.form) return;

      this.form.addEventListener('submit', async (e) => {
        if (!this.root || !document.body.contains(this.root)) return;

        const stueck = this.getStueckFromQtyInput();
        if (stueck <= 0) {
          e.preventDefault(); e.stopPropagation();
          this.updateAtcState();
          return;
        }

        e.preventDefault(); e.stopPropagation();

        try {
          await this.addToCartBatch();
          window.setTimeout(() => { openMiniCart(); }, 0);
        } catch (err) {
          console.error('[sockelleisten-calculator] addToCart failed:', err);
          alert('Leider ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
          if (this.atcButton) {
            this.atcButton.disabled = false;
            this.atcButton.classList.remove('is-loading', 'btn--loading');
          }
        }
      }, true);
    }

    bindAtcClickFallback() {
      if (!this.atcButton) return;

      const isInsideForm = !!(this.form && this.form.contains(this.atcButton));
      if (isInsideForm) return;

      this.atcButton.addEventListener('click', async (e) => {
        const stueck = this.getStueckFromQtyInput();
        if (stueck <= 0) {
          e.preventDefault(); e.stopPropagation();
          this.updateAtcState();
          return;
        }

        e.preventDefault(); e.stopPropagation();

        try {
          await this.addToCartBatch();
          window.setTimeout(() => { openMiniCart(); }, 0);
        } catch (err) {
          console.error('[sockelleisten-calculator] addToCart failed:', err);
          alert('Leider ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
          if (this.atcButton) {
            this.atcButton.disabled = false;
            this.atcButton.classList.remove('is-loading', 'btn--loading');
          }
        }
      }, true);
    }

    // Build cart items
    buildCartItems() {
      const items = [];

      const v = this.currentVariant || this.getCurrentVariantSafe();
      const mainVariantId = Number(v?.id || this.initialVariantId) || 0;
      const stueck = this.getStueckFromQtyInput();

      const fcBase = {
        _fc: '1',
        _fc_type: 'sockelleisten',
        _fc_pid: String(this.productId || this.handle || ''),
        _fc_parent: String(this.bundleId || ''),
        _fc_bundle: String(this.bundleId || ''),
      };

      if (mainVariantId && stueck > 0) {
        // Store DELIVERED lfm (stueck × lfmPerStueck) fractional for cart precision.
        const lfmPer = Number(this.lfmPerStueck) || 0;
        const deliveredLfm = lfmPer > 0 ? roundTo(stueck * lfmPer, 2) : 0;

        const props = {
          ...fcBase,
          _fc_role: 'main',
          _fc_kind: 'sockelleisten',
          _fc_unit: 'lfm',
          _fc_total: String(deliveredLfm),
          lfm: String(deliveredLfm),
          _fc_lfm_per_stueck: String(Number(this.lfmPerStueck) || 1),
        };

        items.push({ id: mainVariantId, quantity: stueck, properties: props });
      }

      // Addons
      const store = this.readAddonsStore();

      const addSelected = (obj) => {
        const map = obj && typeof obj === 'object' ? obj : {};
        Object.keys(map).forEach((vid) => {
          const vIdNum = Number(vid || 0);
          const qty = Number(map[String(vid)] || 0) || 0;
          if (!vIdNum || qty <= 0) return;

          const catItem = this.accessoriesByVariant.get(String(vIdNum));
          const title = String(catItem?.title || '');
          const isOfferByTitle = /angebot/i.test(title);
          const isDiscounted = (Number(catItem?.compareAtCents || 0) || 0) > (Number(catItem?.priceCents || 0) || 0);
          const removeWithMain = (isOfferByTitle || isDiscounted);

          items.push({
            id: vIdNum,
            quantity: qty,
            properties: {
              ...fcBase,
              _fc_remove_with_main: removeWithMain ? '1' : '0',
              _fc_role: removeWithMain ? 'dep' : 'addon',
              _fc_group: removeWithMain ? 'offer' : 'addon',
              _fc_kind: removeWithMain ? 'offer' : 'addon',
            }
          });
        });
      };

      addSelected(store.sockel);
      addSelected(store.verlege);

      return items;
    }

    async addToCartBatch() {
      const desired = this.buildCartItems();
      if (!desired.length) return;

      if (this.atcButton) {
        this.atcButton.disabled = true;
        this.atcButton.classList.add('is-loading', 'btn--loading');
      }

      const cart = await fetchCartJson();

      const bySig = new Map();
      const makeSig = (vid, props) => {
        const p = props && typeof props === 'object' ? props : {};
        const parent = String(p._fc_parent || '').trim();
        const pid = String(p._fc_pid || '').trim();
        const role = String(p._fc_role || '').trim();
        const group = String(p._fc_group || '').trim();
        return `${vid}::${parent}::${pid}::${group}::${role}`;
      };

      (cart.items || []).forEach((it) => {
        const vid = Number(it.variant_id || 0);
        if (!vid) return;

        const props = it.properties || {};
        const hasFc = props && String(props._fc || '') === '1';
        if (!hasFc) return;

        const parent = String(props._fc_parent || '').trim();
        const pid = String(props._fc_pid || '').trim();

        if (!parent || !pid) return;

        const sig = makeSig(vid, props);
        if (!bySig.has(sig)) bySig.set(sig, { key: it.key, hasFc: true });
      });

      const toChange = [];
      const toAdd = [];

      desired.forEach((d) => {
        const vid = Number(d.id || 0);
        if (!vid) return;

        const props = d.properties || {};
        const sig = makeSig(vid, props);
        const existing = bySig.get(sig);

        if (existing && existing.key) {
          toChange.push({ key: existing.key, quantity: Number(d.quantity) || 0, properties: props });
        } else {
          toAdd.push(d);
        }
      });

      for (const ch of toChange) await cartChangeByKey(ch.key, ch.quantity, ch.properties);
      if (toAdd.length) await cartAddItems(toAdd);

      try { document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true })); } catch (_) {}
      try { document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (_) {}
      try { document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true })); } catch (_) {}

      if (this.atcButton) {
        this.atcButton.disabled = false;
        this.atcButton.classList.remove('is-loading', 'btn--loading');
      }
    }
  }

  function boot() {
    const root = document.querySelector(SELECTORS.root);
    if (!root) return;
    const calc = new SockelleistenCalculator(root);
    calc.init();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();